import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Plus, Save, Trash2, X } from "lucide-react";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export function RejectReasonDialog({ count, reasons, busy, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const isOther = reason === "Other";
  const canSave = !isOther || note.trim();
  const options = [...(reasons || []), "Other"];

  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-lg" data-testid="reject-reason-dialog">
        <DialogHeader>
          <DialogTitle>Reject {count > 1 ? `${count} requests` : "this request"}?</DialogTitle>
          <DialogDescription className="text-xs">
            Rejected requests are never sent. Pick a reason (optional) — it&apos;s saved on {count > 1 ? "each one" : "the request"}.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-1.5" role="radiogroup">
          {options.map((r) => (
            <button
              key={r}
              type="button"
              role="radio"
              aria-checked={reason === r}
              onClick={() => setReason(reason === r ? "" : r)}
              className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${reason === r ? "bg-rose-600 text-white border-rose-600" : "bg-white text-zinc-800 border-zinc-300 hover:border-zinc-500"}`}
              data-testid={`reject-reason-${slug(r)}`}
            >{r}</button>
          ))}
        </div>
        <div>
          <label className="text-2xs text-zinc-600">
            Note {isOther ? <span className="text-rose-600">(required for Other)</span> : <span className="text-zinc-400">(optional)</span>}
          </label>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="reject-reason-note" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="reject-reason-cancel">Cancel</Button>
          <Button
            onClick={() => onConfirm(reason || null, note.trim() || null)}
            disabled={!canSave || busy}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="reject-reason-confirm"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <X className="h-3.5 w-3.5 mr-1" />}
            Reject{count > 1 ? ` ${count}` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RejectReasonsCard() {
  const [list, setList] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/reviews/settings").then((r) => setList(r.data?.reject_reasons || [])).catch(() => setList([]));
  }, []);

  function add() {
    const v = draft.trim();
    if (!v) return;
    if (list.some((x) => x.toLowerCase() === v.toLowerCase())) { toast.error("That reason is already in the list"); return; }
    setList([...list, v]);
    setDraft("");
  }

  async function save() {
    setBusy(true);
    try {
      const r = await api.patch("/reviews/settings", { reject_reasons: list.map((x) => x.trim()).filter(Boolean) });
      setList(r.data?.reject_reasons || []);
      toast.success("Reject reasons saved");
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }

  return (
    <Card data-testid="settings-reject-reasons">
      <CardHeader>
        <CardTitle className="text-base">Reject reasons</CardTitle>
        <p className="text-xs text-zinc-500">Quick picks when rejecting a request. &quot;Other&quot; is always available and asks for a note.</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {list === null ? (
          <div className="text-sm text-zinc-500"><Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Loading…</div>
        ) : (
          <>
            <ul className="space-y-1.5">
              {list.map((r, i) => (
                <li key={i} className="flex items-center gap-2" data-testid={`reject-reason-item-${i}`}>
                  <Input
                    value={r}
                    onChange={(e) => setList(list.map((x, j) => (j === i ? e.target.value : x)))}
                    className="h-8 text-sm"
                    data-testid={`reject-reason-input-${i}`}
                  />
                  <Button
                    size="sm" variant="ghost" className="text-rose-600"
                    onClick={() => setList(list.filter((_, j) => j !== i))}
                    data-testid={`reject-reason-remove-${i}`}
                  ><Trash2 className="h-3.5 w-3.5" /></Button>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2">
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                placeholder="Add a reason…"
                className="h-8 text-sm"
                data-testid="reject-reason-new-input"
              />
              <Button size="sm" variant="outline" onClick={add} data-testid="reject-reason-add">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add
              </Button>
            </div>
            <Button onClick={save} disabled={busy} data-testid="reject-reasons-save">
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
              Save reasons
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
